import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { createContactSubmission } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createContactSubmission(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-neutral py-20" data-testid="contact-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 rounded-2xl shadow-sm">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Message Sent!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for contacting us. We'll get back to you as soon as possible.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  full_name: '',
                  email: '',
                  phone: '',
                  subject: '',
                  message: ''
                });
              }}
              className="bg-brand-orange hover:bg-brand-orange/90 text-white"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              Contact Us
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Have questions or need more information? We're here to help. Reach out to us using the contact form or through our contact information below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-0 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-brand-teal" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-brand-blue mb-1">Address</h3>
                    <p className="text-gray-600 text-sm">Cypress, Texas 77433, USA</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-0 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-brand-orange" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-brand-blue mb-1">Phone</h3>
                    <a href="tel:+18328878254" className="text-gray-600 text-sm hover:text-brand-orange transition-colors">
                      +1 (832) 887-8254
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-0 flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-brand-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-brand-blue mb-1">Email</h3>
                    <a href="mailto:info@behavioralhealthnavigation.org" className="text-gray-600 text-sm hover:text-brand-orange transition-colors break-all">
                      info@behavioralhealthnavigation.org
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-brand-teal to-brand-blue p-6 rounded-2xl text-white">
                <CardContent className="p-0">
                  <h3 className="font-heading font-bold text-lg mb-3">Website</h3>
                  <a 
                    href="https://www.behavioralhealthnavigation.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/90 hover:text-white text-sm transition-colors"
                  >
                    www.behavioralhealthnavigation.org
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2 bg-white rounded-2xl shadow-sm">
              <CardContent className="p-8">
                <h2 className="font-heading text-2xl font-bold text-brand-blue mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        required
                        data-testid="full-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        required
                        data-testid="subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      rows={5}
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                      data-testid="message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white py-6 text-lg"
                    data-testid="submit-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                    <Send size={20} className="ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
